import { FieldChangeEvent, Text, TextField, useFormField } from '@weave/design-system';
import { theme } from '@weave/theme-original';
import {
  ComponentProps,
  KeyboardEvent,
  ReactNode,
  UIEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type ListProps = {
  list: { id: string; label: string }[];
  title?: string;
  width?: number | string;
  maxHeight?: number | string;
  canSearch?: boolean;
  searchLabel?: string;
  searchName?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  filterLocally?: boolean;
  onReachEnd?: () => void;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  renderItem: (
    item: { id: string; label: string },
    options: ListRenderItemOptions
  ) => ReactNode;
};

export type ListRenderItemOptions = {
  isActive: boolean;
  listItemProps: ComponentProps<'li'> & { isActive?: boolean };
  focusableProps: {
    ref: (element: HTMLElement | null) => void;
    onFocus: () => void;
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
    tabIndex: number;
  };
};

const getFieldChangeValue = (event?: FieldChangeEvent<string>) => {
  if (!event) {
    return '';
  }

  if ('value' in event) {
    return event.value ?? '';
  }

  if ('target' in event) {
    return (event.target as HTMLInputElement | undefined)?.value ?? '';
  }

  return '';
};

export function List({
  list,
  title,
  width,
  maxHeight = 300,
  canSearch,
  searchLabel,
  searchName,
  searchValue,
  onSearchChange,
  filterLocally = true,
  onReachEnd,
  hasMore,
  isFetchingMore,
  renderItem,
}: ListProps) {
  const searchField = useFormField({
    type: 'text',
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);
  const isFetchingMoreRef = useRef(!!isFetchingMore);

  useEffect(() => {
    isFetchingMoreRef.current = !!isFetchingMore;
  }, [isFetchingMore]);

  const effectiveSearchValue = searchValue ?? searchField.value;

  const filteredList = useMemo(() => {
    if (!filterLocally) {
      return list;
    }

    const searchTerm = effectiveSearchValue.toLowerCase();
    return list.filter((item) => item.label.toLowerCase().includes(searchTerm));
  }, [effectiveSearchValue, filterLocally, list]);

  useEffect(() => {
    if (filteredList.length === 0) {
      setActiveIndex(0);
      return;
    }

    setActiveIndex((currentIndex) => Math.min(currentIndex, filteredList.length - 1));
  }, [filteredList.length]);

  const focusItemAtIndex = (index: number) => {
    itemRefs.current[index]?.focus();
  };

  const handleItemKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
    if (filteredList.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextIndex = (index + 1) % filteredList.length;
      setActiveIndex(nextIndex);
      focusItemAtIndex(nextIndex);
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const nextIndex = (index - 1 + filteredList.length) % filteredList.length;
      setActiveIndex(nextIndex);
      focusItemAtIndex(nextIndex);
    }
  };

  const handleSearchFieldKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (filteredList.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusItemAtIndex(activeIndex === -1 ? 0 : activeIndex);
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const lastIndex = filteredList.length - 1;
      setActiveIndex(lastIndex);
      focusItemAtIndex(lastIndex);
    }
  };

  const handleSearchFieldChange = (event?: FieldChangeEvent) => {
    if (onSearchChange) {
      onSearchChange(getFieldChangeValue(event));
      return;
    }

    searchField.onChange(event);
  };

  const handleListScroll = (event: UIEvent<HTMLUListElement>) => {
    if (!onReachEnd || !hasMore || isFetchingMoreRef.current) {
      return;
    }

    const scrollContainer = event.currentTarget;
    const remainingScroll =
      scrollContainer.scrollHeight -
      scrollContainer.scrollTop -
      scrollContainer.clientHeight;

    if (remainingScroll <= 48) {
      isFetchingMoreRef.current = true;
      onReachEnd();
    }
  };

  if (list.length === 0) {
    return (
      <div style={{ width: width ?? 300, padding: theme.spacing(2) }}>
        {title && (
          <Text weight="bold" css={{ margin: 0, marginBottom: theme.spacing(2) }}>
            {title}
          </Text>
        )}
        <Text size="medium" css={{ margin: 0 }}>
          No items to display.
        </Text>
      </div>
    );
  }

  return (
    <div
      style={{
        width: width ?? 300,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
      }}
    >
      {title && (
        <Text weight="bold" css={{ margin: 0 }}>
          {title}
        </Text>
      )}
      {canSearch && (
        <TextField
          {...searchField}
          value={effectiveSearchValue}
          name={searchName ?? 'listSearch'}
          label={searchLabel ?? 'Search'}
          onChange={handleSearchFieldChange as (event?: FieldChangeEvent<any>) => void}
          onKeyDown={handleSearchFieldKeyDown}
        />
      )}
      <ul
        onScroll={handleListScroll}
        style={{
          maxHeight,
          overflowY: 'auto',
          padding: 0,
          margin: 0,
          listStyle: 'none',
        }}
      >
        {filteredList.map((item, index) => {
          const isActive = index === activeIndex;

          return renderItem(item, {
            isActive,
            listItemProps: {
              key: item.id,
              isActive,
              onMouseEnter: () => setActiveIndex(index),
              onMouseLeave: () => setActiveIndex(-1),
            },
            focusableProps: {
              ref: (element) => {
                itemRefs.current[index] = element;
              },
              onFocus: () => setActiveIndex(index),
              onKeyDown: (event) => handleItemKeyDown(event, index),
              tabIndex: isActive ? 0 : -1,
            },
          });
        })}
      </ul>
    </div>
  );
}

export const ListItem = ({
  children,
  isActive = false,
  ...rest
}: ComponentProps<'li'> & { isActive?: boolean }) => {
  return (
    <li
      {...rest}
      css={{
        padding: theme.spacing(0.5, 1),
        background: isActive ? theme.colors.gray200 : undefined,

        '&:hover': {
          background: theme.colors.gray200,
          cursor: rest.onClick ? 'pointer' : undefined,
        },
      }}
    >
      {children}
    </li>
  );
};
